import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import type { ProVideo } from '../types/lesson';
import { CLOUD_RUN_CONFIG } from '../config/cloudRun';

const PRO_VIDEOS_COLLECTION = 'proVideos';

/**
 * Get all stock (DB Pro Library) videos.
 */
export async function getStockProVideos(): Promise<ProVideo[]> {
  const q = query(
    collection(db, PRO_VIDEOS_COLLECTION),
    where('isStock', '==', true),
    orderBy('title')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as ProVideo));
}

/**
 * Get pro videos owned by a specific coach.
 */
export async function getCoachProVideos(coachId: string): Promise<ProVideo[]> {
  const q = query(
    collection(db, PRO_VIDEOS_COLLECTION),
    where('coachId', '==', coachId),
    where('isStock', '==', false),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as ProVideo));
}

/**
 * Get all pro videos available to a coach: stock (DB Pro Library) + their own.
 */
export async function getAllProVideosForCoach(coachId: string): Promise<{ stock: ProVideo[]; coach: ProVideo[] }> {
  const [stock, coach] = await Promise.all([
    getStockProVideos(),
    getCoachProVideos(coachId),
  ]);
  return { stock, coach };
}

/**
 * Get a single pro video by ID.
 */
export async function getProVideo(proVideoId: string): Promise<ProVideo | null> {
  const ref = doc(db, PRO_VIDEOS_COLLECTION, proVideoId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { ...snap.data(), id: snap.id } as ProVideo;
}

/**
 * Create a new pro video (stock or coach-owned).
 */
export async function createProVideo(
  data: Omit<ProVideo, 'id' | 'createdAt'>
): Promise<string> {
  const ref = doc(collection(db, PRO_VIDEOS_COLLECTION));
  const id = ref.id;
  const clean = Object.fromEntries(
    Object.entries({ ...data, id, createdAt: serverTimestamp() }).filter(([, v]) => v !== undefined)
  );
  await setDoc(ref, clean);
  return id;
}

/**
 * Update an existing pro video.
 */
export async function updateProVideo(
  proVideoId: string,
  updates: Partial<Omit<ProVideo, 'id' | 'createdAt'>>
): Promise<void> {
  const ref = doc(db, PRO_VIDEOS_COLLECTION, proVideoId);
  const clean = Object.fromEntries(
    Object.entries({ ...updates, updatedAt: serverTimestamp() }).filter(([, v]) => v !== undefined)
  );
  await updateDoc(ref, clean);
}

/**
 * Delete a pro video document (does not delete storage files — those remain so
 * any lesson recordings that referenced the old URL stay valid).
 */
export async function deleteProVideo(proVideoId: string): Promise<void> {
  const ref = doc(db, PRO_VIDEOS_COLLECTION, proVideoId);
  await deleteDoc(ref);
}

/**
 * Trigger Cloud Run to produce a web-optimized (≤1080p, fast-start) copy.
 * Updates the proVideos/{proVideoId} document with the result and returns
 * the new URLs so callers can update local state without a re-fetch.
 */
export async function preprocessProVideoForWeb(
  proVideoId: string,
  sourcePath: string,
  videoUrl: string
): Promise<{ webOptimizedVideoUrl: string; webOptimizedStoragePath: string; durationSeconds: number }> {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('User not authenticated');

  const idToken = await currentUser.getIdToken();
  const response = await fetch(`${CLOUD_RUN_CONFIG.VIDEO_PROCESSOR_URL}/preprocess-pro-video`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ proVideoId, sourcePath, videoUrl }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || `Pro video preprocessing failed: ${response.status}`);
  }

  const data = await response.json();
  return {
    webOptimizedVideoUrl: data.webOptimizedVideoUrl,
    webOptimizedStoragePath: data.webOptimizedStoragePath,
    durationSeconds: data.durationSeconds,
  };
}

/**
 * Get all pro videos regardless of ownership (admin use only).
 */
export async function getAllProVideos(): Promise<ProVideo[]> {
  const snapshot = await getDocs(collection(db, PRO_VIDEOS_COLLECTION));
  return snapshot.docs
    .map((d) => ({ ...d.data(), id: d.id } as ProVideo))
    .sort((a, b) => {
      if (a.isStock !== b.isStock) return a.isStock ? -1 : 1;
      return a.title.localeCompare(b.title);
    });
}
